import assign from 'lodash/assign';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import has from 'lodash/has';
import find from 'lodash/find';
import uiRouter from '@uirouter/angularjs';

import 'constants/constants.json';
import { CheckoutService } from 'services/checkout.service';
import { FacebookPixelService } from 'services/facebookPixel.service';
import { PaypalSdkService } from 'services/paypalSdk.service';
import { GaService } from 'services/ga.service';
import { ApiService } from 'services/api.service';
import { AuthService } from 'services/auth.service';
import { AddressVerificationService } from 'services/addressVerification.service';
import { ShopperAccountService } from 'services/shopperAccount.service';
import {
    isShippingRequiredOnCart,
    isShippingAndBillingAddressSame,
    hasValidShippingMethod,
    clearSavedStoreCreditInfo,
    getSavedStoreCreditInfo,
    setSavedStoreCreditInfo,
    getShippingMethodIndex,
    getCartTotalAmountInCents,
    getSavedCartInfo,
    setSavedCartInfo,
    dollarsToCents
} from 'services/cartUtils';
import { OrderService } from 'services/order.service';
import { createStripeTokenMetadata } from 'services/creditCard.service';
import {
    logCheckoutStarted,
    logShopperInfo,
    logShippingInfo,
    logPaymentMethod,
    logPurchase,
    logPageView
} from 'services/dataLayer';

import { Accordian } from 'components/accordian/accordian';
import { AddressForm } from 'components/addressForm/addressForm';
import { CheckoutWithPaypalSdkButton } from 'components/checkoutWithPaypalSdkButton/checkoutWithPaypalSdkButton';
import { CheckoutWithPaypalSdkSummaryButton }
    from 'components/checkoutWithPaypalSdkSummaryButton/checkoutWithPaypalSdkSummaryButton';
import { ContinueButton } from 'components/continueButton/continueButton';
import { PaymentMethodForm } from 'components/paymentMethodForm/paymentMethodForm';
import { ContactInfoForm } from 'components/contactInfoForm/contactInfoForm';
import { AddressVerificationForm } from 'components/addressVerificationForm/addressVerificationForm';
import { ShippingAddressForm } from 'components/shippingAddressForm/shippingAddressForm';
import { DisplayContactAndDelivery } from 'components/displayContactAndDelivery/displayContactAndDelivery';
import { ShippingMethodForm } from 'components/shippingMethodForm/shippingMethodForm';
import { DisplayShippingMethod } from 'components/displayShippingMethod/displayShippingMethod';
import { ThankYou } from 'components/thankYou/thankYou';
import { DisplayBillingAndPayment } from 'components/displayBillingAndPayment/displayBillingAndPayment';
import { DisplayCountryName } from 'components/displayCountryName/displayCountryName';
import { DisplayStateName } from 'components/displayStateName/displayStateName';
import { FullscreenLoaderService } from 'components/fullscreenLoader/fullscreenLoader.service';
import { Header } from 'components/header/header';
import { EditShippingMethod } from 'components/editShippingMethod/editShippingMethod';
import { EditContactAndDelivery } from 'components/editContactAndDelivery/editContactAndDelivery';
import { HeaderBackground } from 'components/headerBackground/headerBackground';
import { PromoCodeLink } from 'components/promoCodeLink/promoCodeLink';
import { EditStepButton } from 'components/editStepButton/editStepButton';
import { LoginButton } from 'components/loginButton/loginButton';
import { SecondaryLoginButton } from 'components/secondaryLoginButton/secondaryLoginButton';
import { EditAccountContactAndDelivery }
    from 'components/editAccountContactAndDelivery/editAccountContactAndDelivery';
import { EditAccountPayment } from 'components/editAccountPayment/editAccountPayment';
import { LoginModal } from 'components/loginModal/loginModal';
import { stripeElementCardStyle } from 'components/stripeElementsForm/stripeElementsForm';

import { OrderSummary } from './components/orderSummary/orderSummary';
import template from './newCheckout.html';

const noShippingMethodsIndex = -1;
const DEFAULT_COUNTRY = 'US';

const STEP_ONE_HEADING_TEXT_NO_SHIPPING = 'Contact & Billing';
const STEP_ONE_HEADING_TEXT_WITH_SHIPPING = 'Contact & Delivery';
const STEP_TWO_HEADING_TEXT = 'Shipping Method';
const STEP_THREE_HEADING_TEXT = 'Payment & Review';

export const NewCheckoutComponent = angular
    .module('checkout.newCheckout', [
        uiRouter,
        'app.constants',
        AddressVerificationForm,
        AddressVerificationService,
        CheckoutService,
        FacebookPixelService,
        PaypalSdkService,
        GaService,
        ApiService,
        AuthService,
        ShopperAccountService,
        OrderService,
        Accordian,
        AddressForm,
        CheckoutWithPaypalSdkButton,
        CheckoutWithPaypalSdkSummaryButton,
        ContinueButton,
        OrderSummary,
        PaymentMethodForm,
        ContactInfoForm,
        ShippingAddressForm,
        DisplayContactAndDelivery,
        ShippingMethodForm,
        DisplayShippingMethod,
        ThankYou,
        DisplayBillingAndPayment,
        DisplayCountryName,
        DisplayStateName,
        FullscreenLoaderService,
        Header,
        EditShippingMethod,
        EditContactAndDelivery,
        HeaderBackground,
        PromoCodeLink,
        EditStepButton,
        LoginButton,
        SecondaryLoginButton,
        EditAccountContactAndDelivery,
        EditAccountPayment,
        LoginModal
    ]).component('newCheckout', {
        template,
        bindings: {
            cart: '<',
            bag: '<',
            tenantId: '<',
            storeInfo: '<',
            returnUrl: '<',
            availablePaymentMethods: '<',
            isLoggedIn: '<',
            shippingMethods: '<',
            useAddressVerification: '<',
            stripePublishableKey: '<'
        },
        controller: class CheckoutCtrl {
            constructor(
                $timeout,
                $anchorScroll,
                $stateParams,
                $rootScope,
                $scope,
                $mdMedia,
                $location,
                $window,
                $q,
                addressVerificationService,
                gaService,
                facebookPixelService,
                checkoutService,
                paypalSdkService,
                editContactAndDeliveryService,
                editShippingMethodService,
                editAccountContactAndDeliveryService,
                editAccountPaymentService,
                apiService,
                loginModalService,
                authService,
                orderService,
                shopperAccountService,
                loadingService,
                GA_CHECKOUT_STEPS,
                scriptLoaderService
            ) {
                assign(this, {
                    $timeout,
                    $anchorScroll,
                    $stateParams,
                    $rootScope,
                    $scope,
                    $mdMedia,
                    $location,
                    $window,
                    $q,
                    addressVerificationService,
                    gaService,
                    facebookPixelService,
                    checkoutService,
                    paypalSdkService,
                    editContactAndDeliveryService,
                    editShippingMethodService,
                    editAccountContactAndDeliveryService,
                    editAccountPaymentService,
                    apiService,
                    loginModalService,
                    authService,
                    orderService,
                    shopperAccountService,
                    loadingService,
                    curbsideShippingAvailable: false,
                    GA_CHECKOUT_STEPS,
                    scriptLoaderService,
                    isBillingAddressVerified: false,
                    isShippingAddressVerified: false,

                    steps: {
                        contactAndDelivery: { number: 1, complete: false },
                        shippingMethod: { number: 2, complete: false },
                        paymentAndReview: { number: 3, complete: false }
                    },
                    currentStep: 1,
                    selectedPaymentMethod: 'Credit Card',
                    shippingAddressModel: {
                        shippingAsBilling: true,
                        country: DEFAULT_COUNTRY
                    },
                    billingAddressModel: {
                        country: DEFAULT_COUNTRY
                    },
                    showPromoInput: false,
                    orderSummaryCollapsed: true,
                    saveLoggedInShopperInfo: true,
                    availableShippingMethods: [],
                    stepTwoHeadingText: STEP_TWO_HEADING_TEXT,
                    stepThreeHeadingText: STEP_THREE_HEADING_TEXT,
                    storeCreditInfo: { items: [], storeCreditAmountInCents: 0 }
                });

                this.updateCreditCardType = this.updateCreditCardType.bind(this);
                this.onStripeElementsCardChange = this.onStripeElementsCardChange.bind(this);
                this.removeDiscountCodeItem = this.removeDiscountCodeItem.bind(this);
                this.removeStoreCreditItem = this.removeStoreCreditItem.bind(this);
                this.applyPromoCode = this.applyPromoCode.bind(this);
                this.onOrderNotesChanged = this.onOrderNotesChanged.bind(this);
                this.onCheckoutClicked = this.onCheckoutClicked.bind(this);
                this.updateAddress = this.updateAddress.bind(this);
                this.getGiftCards = this.getGiftCards.bind(this);
            }

            $onInit() {
                this.cartAndSavedInfo = {
                    ...this.cart,
                    ...getSavedCartInfo()
                };
                this.contactInfoModel = this.cartAndSavedInfo.contactInfo || this.contactInfoModel;
                this.shippingAddressModel = this.cartAndSavedInfo.shippingAddress || this.shippingAddressModel;
                this.billingAddressModel = this.cartAndSavedInfo.billingAddress || this.billingAddressModel;
                this.orderNotes = this.cartAndSavedInfo.orderNotes || '';

                this.loadingService.hide(); // Needed for logging out, which reloads state

                this.storeCreditInfo = getSavedStoreCreditInfo();

                if (this.cart) {
                    this.setAvailableShippingMethods(this.shippingMethods);
                    this.fetchShippingMethods();
                    this.currentStep = this.steps.contactAndDelivery.number;
                    this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.entrance, null, this.cartAndSavedInfo);
                    logPageView(this.tenantId);
                    logCheckoutStarted(this.tenantId, this.cartAndSavedInfo, this.returnUrl);

                    this.selectedPaymentMethod =
                        this.checkoutService.getOptimalDefaultPaymentMethod(this.availablePaymentMethods);

                    if (this.paypalSdkService.paypalIsAvailablePaymentMethod(this.storeInfo)) {
                        this.paypalSdkService.init({
                            storeInfo: this.storeInfo,
                            cartId: this.cart.cartId,
                            bagId: this.bag.id,
                            total: this.cart.grandTotal,
                            newCarts: true,
                            getGiftCards: this.getGiftCards
                        });
                    }
                }
                if (this.paypalSdkService.hasPaypalQueryParams(this.$stateParams)) {
                    this.initPaypalReturn();

                    if (this.isLoggedIn) {
                        // TODO update initShopperAccount to work with new carts it currently sets shipping address
                        // on the cart, but will need to be updated to store the shipping address elsewhere as new
                        // carts don't deal with shipping address
                        this.initShopperAccount(true);
                    }
                } else {
                    if (this.isLoggedIn) {
                        this.initShopperAccount();
                    }
                    this.setStateFromCart();
                    logCheckoutStarted(this.tenantId, this.cartAndSavedInfo, this.returnUrl);
                }

                this.getTax();

                if (has(this.storeInfo, 'thirdPartyIntegrations.facebookTrackingPixelId')) {
                    this.facebookPixelService.trackInitiateCheckoutEvent(this.cart, this.grandTotal);
                }
                this.$scope.$watch(() => this.$mdMedia('xs'), (xs) => {
                    this.isMobile = xs;
                });

                if (this.storeInfo.acceptsStripeAsPayment) {
                    this.initStripeElements();
                }
            }

            getGiftCards() {
                return this.storeCreditInfo.storeCreditAmountInCents;
            }

            updateCartWithShopper(shopperId, shopperEmail) {
                this.apiService.updateCart(this.cart.cartId, {
                    shopperId: shopperId,
                    shopperEmail: shopperEmail
                })
                    .then(() => this.apiService.getCart(this.cart.cartId))
                    .then(cart => this.cart = cart)
                    .then(() => this.getTax());
            }

            getTax() {
                const shippingAmount = get(this.cartAndSavedInfo, 'shippingMethod.shippingPriceInCents');
                const hasShippingAddress = this.shippingAddressModel && this.shippingAddressModel.address1;

                if (hasShippingAddress && Number.isInteger(shippingAmount)) {
                    this.apiService.getTax(
                        this.cart.cartId,
                        shippingAmount,
                        {
                            address1: this.shippingAddressModel.address1,
                            address2: this.shippingAddressModel.address2,
                            city: this.shippingAddressModel.city,
                            state: this.shippingAddressModel.state,
                            postalCode: this.shippingAddressModel.postalCode,
                            country: this.shippingAddressModel.country
                        }
                    ).then(({ taxAmount, provider }) => {
                        this.taxAmountInCents = taxAmount.amount;
                        this.taxProvider = provider;
                        this.setStateFromCart();
                    });
                }
            }

            verifyBillingAddress() {
                if (this.isBillingAddressVerified === true) {
                    return this.$q.resolve();
                }

                return this.addressVerificationService.isAddressVerified(this.cartAndSavedInfo.billingAddress)
                    .then(verified => this.isBillingAddressVerified = verified);
            }

            verifyShippingAddress() {
                if (this.isShippingAddressVerified === true) {
                    return this.$q.resolve();
                }

                return this.addressVerificationService.isAddressVerified(this.cartAndSavedInfo.shippingAddress)
                    .then(verified => this.isShippingAddressVerified = verified);
            }

            verifyShippingAndBillingAddresses() {
                this.$rootScope.$evalAsync(() => {
                    this.$q.all([
                        this.verifyBillingAddress(),
                        this.verifyShippingAddress()
                    ]);
                });
            }

            setStateFromCart(throwErrorForNoShippingMethods = false) {
                this.storeCreditInfo = this.applyGiftCardTotals(this.storeCreditInfo);
                setSavedStoreCreditInfo(this.storeCreditInfo);

                this.grandTotal = (this.cartGrandTotalInCents() - this.storeCreditInfo.storeCreditAmountInCents) / 100;

                this.selectedShippingMethodIndex = this.getSelectedShippingMethodIndexOrDefault();
                this.isShippingRequired = isShippingRequiredOnCart(this.cart.items);
                this.steps.contactAndDelivery.complete = this.isContactAndDeliveryComplete();

                if (this.cartAndSavedInfo.shippingAddress) {
                    this.shippingAddressModel =
                        get(this.cartAndSavedInfo, 'shippingAddress', this.shippingAddressModel);
                    this.contactInfoModel = get(this.cartAndSavedInfo, 'contactInfo', this.contactInfoModel);

                    if (!this.isShippingRequired && this.steps.contactAndDelivery.complete) {
                        this.updateCartInfo({
                            billingAddress: this.cartAndSavedInfo.shippingAddress
                        });
                    }
                }
                this.billingSameAsShipping = isShippingAndBillingAddressSame(this.cartAndSavedInfo);
                if (this.billingSameAsShipping) {
                    this.billingAddressModel = this.cartAndSavedInfo.billingAddress;
                }

                if (this.useAddressVerification) {
                    this.verifyShippingAndBillingAddresses();
                }

                if (this.isShippingStepComplete(this.cartAndSavedInfo.shippingMethod)) {
                    this.steps.shippingMethod.complete = true;
                }
                if (throwErrorForNoShippingMethods) this.throwIfNoShippingMethods();
                let currentStep = this.steps.contactAndDelivery.complete ?
                    this.steps.shippingMethod.number : this.steps.contactAndDelivery.number;
                if (this.steps.shippingMethod.complete) {
                    currentStep = this.steps.paymentAndReview.number;
                }
                this.currentStep = currentStep;
                this.stepOneHeadingText = this.isShippingRequired
                    ? STEP_ONE_HEADING_TEXT_WITH_SHIPPING : STEP_ONE_HEADING_TEXT_NO_SHIPPING;

                this.updateCartInfo();
            }

            initStripeElements() {
                this.$q.all([
                    this.scriptLoaderService.loadScriptTagPromise({
                        src: 'https://js.stripe.com/v3',
                        async: true
                    }),
                    this.apiService.getStripePublishableKey()
                ]).then(([, { stripePublishableKey }]) => {
                    // eslint-disable-next-line new-cap
                    this.stripe = window.Stripe(stripePublishableKey);
                    this.stripeElements = this.stripe.elements();
                    this.stripeElementsCard = this.stripeElements.create('card', {
                        style: stripeElementCardStyle,
                        hidePostalCode: true
                    });
                });
            }

            isShippingStepComplete(shippingMethod) {
                return (this.steps.contactAndDelivery.complete && !this.isShippingRequired)
                    || (this.hasValidShippingSelection()
                        && (this.didContinueStepTwo ||
                            hasValidShippingMethod(this.availableShippingMethods, shippingMethod)));
            }

            applyGiftCardTotals(storeCreditInfo) {
                let grandTotalInCents = this.cartGrandTotalInCents();
                let storeCreditAmountInCents = 0;
                const items = storeCreditInfo.items.map((item) => {
                    if (grandTotalInCents > item.balance) {
                        const amountUsedInCents = item.balance;
                        storeCreditAmountInCents += item.balance;
                        grandTotalInCents -= item.balance;
                        return {
                            ...item,
                            amountUsedInCents
                        };
                    }
                    if (grandTotalInCents < item.balance) {
                        const amountUsedInCents = grandTotalInCents;
                        storeCreditAmountInCents += grandTotalInCents;
                        grandTotalInCents = 0;
                        return {
                            ...item,
                            amountUsedInCents
                        };
                    }
                    // only possibility left: grandTotalInCents === item.balance
                    const amountUsedInCents = grandTotalInCents;
                    storeCreditAmountInCents += grandTotalInCents;
                    grandTotalInCents = 0;
                    return {
                        ...item,
                        amountUsedInCents
                    };
                });
                return {
                    items,
                    storeCreditAmountInCents
                };
            }

            hasValidShippingSelection() {
                return angular.isDefined(this.selectedShippingMethodIndex)
                    && (!this.hasNoShippingMethodsForStore() || this.cartAndSavedInfo.shippingMethod)
                    && this.cartAndSavedInfo.shippingAddress && this.cartAndSavedInfo.contactInfo
                    && angular.isArray(this.availableShippingMethods)
                    && this.availableShippingMethods.length > 0;
            }

            hasNoShippingMethodsForStore() {
                return this.selectedShippingMethodIndex === noShippingMethodsIndex;
            }

            throwIfNoShippingMethods() {
                if (this.hasNoShippingMethodsForStore()) {
                    throw new Error('This store does not have any shipping methods configured.');
                }
            }

            updateAddress(newAddress) {
                this.verificationRecommendations = null;
                this.verificationChanges = null;

                if (!newAddress) { // edit address again
                    this.steps.contactAndDelivery.complete = false;
                    this.showAddressSuggestion = false;
                } else {
                    this.shippingAddressModel = { ...newAddress };
                    this.steps.contactAndDelivery.complete = true;
                    this.showAddressSuggestion = false;

                    this.isUpdatingCart = true;
                    this.isStepOneLoading = true;
                    this.setContactInfoAndShippingAddress();
                }
            }

            onStepOneContinueClicked() {
                if (this.contactAndDeliveryForm.$invalid) {
                    this.checkoutService.forceFormValidation(this.contactAndDeliveryForm['$ctrl.contactInfoForm']);
                    this.checkoutService.forceFormValidation(this.contactAndDeliveryForm['$ctrl.addressForm']);
                    return;
                }

                this.isUpdatingCart = true;
                this.isStepOneLoading = true;

                if (this.useAddressVerification) {
                    // check address validity and display recommendations (if needed)
                    this.addressVerificationService.verifyAddress(this.shippingAddressModel)
                        .then(({ verified, changes, recommendations }) => {
                            if (!verified || changes) {
                                this.isUpdatingCart = false;
                                this.isStepOneLoading = false;

                                this.showAddressSuggestion = true;
                                this.verificationRecommendations = recommendations;
                                this.verificationChanges = changes;
                            } else {
                                this.setContactInfoAndShippingAddress();
                            }
                        });
                } else {
                    this.setContactInfoAndShippingAddress();
                }
            }

            setContactInfoAndShippingAddress() {
                this.checkoutService.saveContactInfoAndShippingAddress({
                    contactInfoModel: this.contactInfoModel,
                    shippingAddressModel: this.shippingAddressModel
                }).then(() => {
                    this.updateCartInfo({
                        ...getSavedCartInfo() // session storage was updated outside of component
                    });
                    if (this.isShippingRequired) {
                        this.setStateFromCart();
                        this.scrollToCurrentStep();
                    }
                    this.trackStepOneEvents();
                    logShopperInfo(this.tenantId, this.cartAndSavedInfo);

                    (this.$window.dataLayer || []).push({
                        event: 'contact_info',
                        contact_email: this.contactInfoModel.email,
                        contact_first_name: this.shippingAddressModel.firstName,
                        contact_last_name: this.shippingAddressModel.lastName,
                        contact_country: this.shippingAddressModel.country,
                        contact_address: this.shippingAddressModel.address1,
                        contact_city: this.shippingAddressModel.city,
                        contact_state: this.shippingAddressModel.state,
                        contact_zip: this.shippingAddressModel.postalCode
                    });
                    if (
                        this.loggedInShopper &&
                    this.cartAndSavedInfo.contactInfo.phoneNumber !== undefined &&
                    this.cartAndSavedInfo.contactInfo.phoneNumber !==
                    this.loggedInShopper.contactInformation.phoneNumber
                    ) {
                        this.checkoutService.applyUpdatedPhoneNumberToShopper(
                            this.loggedInShopper, this.cartAndSavedInfo.contactInfo.phoneNumber
                        ).then((updatedShopper) => {
                            if (updatedShopper) {
                                this.loggedInShopper = updatedShopper;
                            }
                        }).catch((err) => {
                            this.checkoutService.showErrorDialog(err);
                        });
                    }

                    this.updateCartInfo();
                    this.getTax();
                })
                    .then(this.fetchShippingMethods.bind(this))
                    .then(() => {
                        if (!this.isShippingRequired) {
                            return this.onStepTwoContinueClicked(true);
                        }
                    })
                    .catch((err) => {
                        this.checkoutService.showErrorDialog(err);
                    })
                    .finally(() => {
                        this.isUpdatingCart = false;
                        this.isStepOneLoading = false;
                    });
            }

            fetchShippingMethods() {
                if (this.cart && get(this.shippingAddressModel, 'country')) {
                    return this.apiService.fetchShippingMethods({
                        cartId: this.cart.cartId,
                        country: this.shippingAddressModel.country,
                        state: this.shippingAddressModel.state
                    }).then(res => this.setAvailableShippingMethods(res));
                }
            }

            setAvailableShippingMethods(availableShippingMethods) {
                this.availableShippingMethods = availableShippingMethods;
                this.curbsideShippingAvailable =
                    !!this.availableShippingMethods.find(method => method.name === 'Curbside Pickup');
            }

            onStepTwoContinueClicked(bypassShipping) {
                if (!bypassShipping && this.shippingMethodStepForm.$invalid) {
                    this.checkoutService.forceFormValidation(this.shippingMethodStepForm);
                    return;
                }
                if (this.hasNoShippingMethodsForStore()) {
                    try {
                        const throwIfNoShipping = this.isShippingRequired;
                        this.setStateFromCart(throwIfNoShipping);
                    } catch (err) {
                        const genericError =
                            'Problem processing order. Please review your Shipping Method and try again.';
                        const errorMessage = get(err, 'message', genericError);
                        this.checkoutService.showErrorDialog(errorMessage);
                    }
                } else {
                    const selectedShippingMethod = this.availableShippingMethods[this.selectedShippingMethodIndex];
                    this.cacheSelectedShippingMethod(selectedShippingMethod);
                    this.didContinueStepTwo = true;
                    const throwIfNoShipping = true;
                    this.setStateFromCart(throwIfNoShipping);
                    this.getTax();
                    this.scrollToCurrentStep();
                    this.trackStepTwoEvents();
                    logShippingInfo(this.tenantId, this.cartAndSavedInfo);
                }
            }

            cacheSelectedShippingMethod(selectedShippingMethod) {
                const shippingPriceInCents = dollarsToCents(selectedShippingMethod.shippingPrice);
                this.updateCartInfo({
                    shippingMethod: {
                        ...selectedShippingMethod,
                        shippingPriceInCents
                    }
                });
            }

            onCheckoutClicked() {
                if (
                    this.selectedPaymentMethod !== 'PayPal' ||
                    this.isReturnFromPaypal ||
                    !this.shouldShowPaymentTypeSelection()
                ) {
                    this.validateAndPlaceOrder();
                }
            }

            onStripeElementsCardChange({ error }) {
                this.stripeElementsError = error;
            }

            validateAndPlaceOrder() {
                if ((this.paymentAndReviewForm && this.paymentAndReviewForm.$invalid) || this.stripeElementsError) {
                    this.checkoutService.forceFormValidation(this.paymentAndReviewForm['$ctrl.addressForm']);
                    return;
                }

                const placeOrderPromise = this.isReturnFromPaypal ? this.placePaypalOrder : this.placeOrder;

                this.isUpdatingCart = true;
                this.loadingService.show('Completing your order...');

                this.trackStepThreeEvents();

                placeOrderPromise.bind(this)().finally(() => {
                    this.isUpdatingCart = false;
                    this.loadingService.hide();
                }).catch((err) => {
                    if (err.message && err.title) {
                        this.checkoutService.showErrorDialog(err.message, err.title);
                    } else {
                        this.handleCheckoutError(err);
                    }
                });
            }

            placeOrder() {
                this.updateCartInfo({
                    billingAddress: this.checkoutService.createCartAddressFromForm(
                        this.accountBillingAddress || this.billingAddressModel)
                });
                const throwIfNoShipping = true;
                this.setStateFromCart(throwIfNoShipping);
                return this.applyShippingMethodToCartIfNeeded()
                    .then(this.sendOrderOptions.bind(this))
                    .then(clearSavedStoreCreditInfo);
            }

            handlePostOrderActions() {
                const postOrderActions = [];
                const orderPaidEntirelyWithGiftCard = !this.shouldShowPaymentTypeSelection();
                const savedCartInfo = { ...this.cartAndSavedInfo };

                if (this.isLoggedIn &&
                    this.saveLoggedInShopperInfo &&
                    this.stripe &&
                    this.stripeElementsCard &&
                    !get(this, 'savedPaymentMethods.length') &&
                    !orderPaidEntirelyWithGiftCard
                ) {
                    // save payment method if shopper doesn't have one
                    postOrderActions.push(
                        this.stripe.createToken(
                            this.stripeElementsCard,
                            createStripeTokenMetadata(savedCartInfo.billingAddress)
                        ).then(response => this.apiService.account.paymentMethod.add(response.token.id, true))
                    );
                } else {
                    postOrderActions.push(this.$q.resolve());
                }

                if (!this.isLoggedIn && this.stripe && this.stripeElementsCard && !orderPaidEntirelyWithGiftCard) {
                    // If order was from anonymous Stripe shopper,
                    // create a second token in case shopper wants to save card post checkout.
                    // This needs to be done before stripe elements is unmounted.
                    postOrderActions.push(
                        this.stripe.createToken(
                            this.stripeElementsCard,
                            createStripeTokenMetadata(savedCartInfo.billingAddress)
                        )
                    );
                }

                return this.$q.all(postOrderActions)
                    .then(([
                        saveShopperAccountPaymentMethod,
                        stripeTokenForAccountCreation
                    ]) => ({
                        saveShopperAccountPaymentMethod,
                        stripeTokenForAccountCreation
                    }));
            }

            sendOrderOptions() {
                const placeOrderOptions = this.assembleOrderOptions();
                if (this.grandTotal <= 0) {
                    placeOrderOptions.paymentMethod = 'Cash';
                }

                return this.orderService.placeOrder(placeOrderOptions)
                    .then(order => this.handlePostOrderActions()
                        .then(({ stripeTokenForAccountCreation }) => ({ order, stripeTokenForAccountCreation }))
                    )
                    .then(({ order, stripeTokenForAccountCreation }) => {
                        this.stripeTokenForAccountCreation = stripeTokenForAccountCreation;
                        this.order = order;
                        this.scrollToId('checkout');
                        logPaymentMethod(this.tenantId, order.paymentMethod);
                        logPurchase(this.tenantId, order, this.returnUrl);
                    });
            }

            assembleOrderOptions() {
                const authorizeNet = this.storeInfo.authorizeNet || {};
                const placeOrderOptions = {
                    authorizeNet,
                    cart: this.cartAndSavedInfo,
                    grandTotal: this.grandTotal,
                    paymentMethod: this.selectedPaymentMethod,
                    creditCardType: this.creditCardType,
                    shopperNote: this.orderNotes,
                    storeCreditInfo: this.storeCreditInfo,
                    stripe: this.stripe,
                    stripeElementsCard: this.stripeElementsCard,
                    taxProvider: this.taxProvider
                };
                if (this.isLoggedIn) {
                    placeOrderOptions.customerId = this.apiService.getShopperId();

                    if (this.selectedCard) {
                        placeOrderOptions.stripeCreditCardId = this.selectedCard.id;
                    } else {
                        placeOrderOptions.card = this.creditCardModel;
                    }
                } else {
                    placeOrderOptions.card = this.creditCardModel;
                }
                return placeOrderOptions;
            }

            handleCheckoutError(response) {
                // TODO: handle invalid discounts error here when V2E-3772 is completed
                // TODO: handle invalid shipping method selected when V2E-3739 is completed

                this.gaService.sendOrderPlacementErrorEvent(response);
                const errorDetail = this.orderService.getErrorDetail(response);
                this.checkoutService.showErrorDialog(errorDetail);
            }

            compareShippingMethodChanges(before) {
                if (!isEqual(before, this.availableShippingMethods)) {
                    const useNewCart = true;
                    this.editShippingMethodService.open(
                        undefined,
                        useNewCart,
                        this.availableShippingMethods,
                        this.cartAndSavedInfo.shippingMethod)
                        .then(this.setStateFromCart.bind(this));
                }
            }

            applyPromoCode(code) {
                if (!code) return this.$q.reject('Cannot apply discount without code');

                this.promoCodeNotFound = false;
                this.promoCodeExpired = false;
                this.inactiveGiftCard = false;
                this.giftCardNotAllowed = false;
                this.giftCardAlreadyApplied = false;
                this.cannotAddMoreStoreCredit = false;
                this.discountCodeExistsInCart = false;
                this.isUpdatingCart = true;

                const storeCreditLengthBeforeChanges = this.storeCreditInfo.items.length;
                const cartDiscountLengthBeforeChanges = this.cart.discounts.length;
                const cartGrandTotal = this.cartGrandTotal();

                return this.checkoutService.applyPromoCode(code, this.storeCreditInfo, this.cart, cartGrandTotal)
                    .then((updatedCart) => {
                        this.cart = updatedCart;

                        this.promoCodeNotFound = false;
                        this.setStateFromCart();
                        this.getTax();
                        if (
                            this.cart.discounts.length === cartDiscountLengthBeforeChanges &&
                            this.storeCreditInfo.items.length === storeCreditLengthBeforeChanges
                        ) {
                            this.promoCodeNotFound = true;
                        }
                    }).catch((error) => {
                        if (error.message === 'inactiveGiftCard') {
                            this.inactiveGiftCard = true;
                        } else if (error.message === 'giftCardNotAllowed') {
                            this.giftCardNotAllowed = true;
                        } else if (error.message === 'cannotAddMoreStoreCredit') {
                            this.cannotAddMoreStoreCredit = true;
                        } else if (error.message === 'giftCardAlreadyApplied') {
                            this.giftCardAlreadyApplied = true;
                        } else if (error.message === 'promoCodeNotFound' || error.status === 404) {
                            this.promoCodeNotFound = true;
                        } else if (error.message === 'discountCodeExistsInCart') {
                            this.discountCodeExistsInCart = true;
                        } else {
                            this.promoCodeExpired = true;
                        }
                    }).finally(() => {
                        this.isUpdatingCart = false;
                    });
            }

            removeDiscountCodeItem(discountCode) {
                if (!this.cart.pricing.appliedDiscounts.length) {
                    return this.$q.reject('No promo code to remove');
                }

                this.promoCodeNotFound = false;
                this.promoCodeExpired = false;
                const shippingMethodsBefore = cloneDeep(this.availableShippingMethods);
                this.isUpdatingCart = true;

                return this.checkoutService.removeDiscountCodeItem(discountCode)
                    .then((updatedCart) => {
                        this.cart = updatedCart;
                        this.setStateFromCart();
                        this.getTax();
                        this.compareShippingMethodChanges(shippingMethodsBefore);
                    }).catch(() => {
                        this.checkoutService.showErrorDialog('Error removing promo code');
                    }).finally(() => {
                        this.isUpdatingCart = false;
                    });
            }

            removeStoreCreditItem(code) {
                const storeCreditIndex = this.storeCreditInfo.items.findIndex(item => item.giftCardCode === code);
                if (storeCreditIndex > -1) {
                    this.storeCreditInfo.items.splice(storeCreditIndex, 1);
                }
                this.setStateFromCart();
            }

            onOrderNotesChanged(orderNotes) {
                this.orderNotes = orderNotes;
                this.updateCartInfo({
                    orderNotes: orderNotes
                });
            }

            getSelectedShippingMethodIndex() {
                if (this.cartAndSavedInfo.shippingMethod) {
                    return getShippingMethodIndex(this.availableShippingMethods, this.cartAndSavedInfo.shippingMethod);
                } else if (!this.availableShippingMethods || !this.availableShippingMethods.length) {
                    // TODO: reference correct availableShippingMethods object
                    return noShippingMethodsIndex;
                }
            }

            getSelectedShippingMethodIndexOrDefault() {
                const defaultIndex = angular.isDefined(this.selectedShippingMethodIndex) ?
                    this.selectedShippingMethodIndex : 0;
                const selectedShippingMethodIndex = this.getSelectedShippingMethodIndex();
                return angular.isDefined(selectedShippingMethodIndex) ? selectedShippingMethodIndex : defaultIndex;
            }

            ensureShippingSelected() {
                this.selectedShippingMethodIndex = this.getSelectedShippingMethodIndexOrDefault();
            }

            onEditStepOneClicked($event) {
                const useNewCarts = true;
                if (this.isLoggedIn &&
                    this.savedShippingAddresses &&
                    this.savedShippingAddresses.items &&
                    this.savedShippingAddresses.items.length) {
                    // TODO: update this to use the new cart
                    this.editAccountContactAndDeliveryService.open(
                        this.loggedInShopper,
                        this.storeInfo.isCheckoutPhoneNumberRequired,
                        this.cartAndSavedInfo.selectedShippingAddressId,
                        this.paypalData,
                        this.isShippingRequired,
                        $event,
                        this.useAddressVerification,
                        useNewCarts
                    ).then(({ shopperAccount }) => {
                        if (shopperAccount) this.loggedInShopper = shopperAccount;
                        this.contactInfoModel =
                            { email: shopperAccount.emailAddress, phoneNumber: shopperAccount.phoneNumber };
                        this.updateCartInfo({
                            ...getSavedCartInfo() // session storage was updated outside of component
                        });
                        this.setStateFromCart();
                        this.getTax();
                        logShopperInfo(this.tenantId, this.cartAndSavedInfo);
                    });
                } else {
                    this.isShippingAddressVerified = false;
                    this.editContactAndDeliveryService.open(
                        this.cartAndSavedInfo,
                        this.storeInfo.isCheckoutPhoneNumberRequired,
                        !this.isReturnFromPaypal && this.isShippingRequired,
                        $event,
                        this.onLoginClicked.bind(this),
                        this.isLoggedIn,
                        this.isShippingRequired,
                        this.useAddressVerification,
                        useNewCarts
                    ).then(() => {
                        this.updateCartInfo({
                            ...getSavedCartInfo() // session storage was updated outside of component
                        });
                        this.setStateFromCart();
                        this.getTax();
                        logShopperInfo(this.tenantId, this.cartAndSavedInfo);
                    });
                }
            }

            onEditStepTwoClicked($event) {
                const isNewCart = true;
                this.editShippingMethodService.open(
                    $event, isNewCart, this.availableShippingMethods, this.cartAndSavedInfo.shippingMethod)
                    .then(({ shippingMethod }) => {
                        this.cacheSelectedShippingMethod(shippingMethod);
                        this.setStateFromCart();
                        this.getTax();
                        logShippingInfo(this.tenantId, this.cartAndSavedInfo);
                    });
            }

            onEditStepThreeClicked($event) {
                if (this.isLoggedIn) {
                    let selectedPaymentMethod;
                    if (this.selectedCard) {
                        selectedPaymentMethod = this.selectedCard.id;
                    } else if (this.isReturnFromPaypal) {
                        selectedPaymentMethod = 'PayPal';
                    } else {
                        selectedPaymentMethod = null;
                    }

                    this.editAccountPaymentService.open(selectedPaymentMethod, this.stripe, $event)
                        .then((paymentMethod) => {
                            if (paymentMethod === 'PayPal') {
                                this.selectedPaymentMethod = paymentMethod;
                            } else {
                                if (this.isReturnFromPaypal) {
                                    this.endPaypalSession();
                                }
                                this.setAccountPaymentInfo(paymentMethod);
                            }
                        });
                } else if (this.isReturnFromPaypal && !this.paypalCancelled) {
                    this.endPaypalSession();
                }
            }

            scrollToCurrentStep() {
                this.scrollToId(`checkout-step-${this.currentStep}`);
            }

            scrollToId(selector) {
                this.$timeout(() => {
                    this.$anchorScroll(selector);
                });
            }

            initPaypalReturn() {
                this.isReturnFromPaypal = true;
                this.loadingService.show('Returning to order review...');
                this.selectedPaymentMethod = 'PayPal';
                this.paypal = {
                    token: this.$stateParams.token,
                    payerId: this.$stateParams.PayerID
                };
                this.paypalSdkService.savePaypalData(this.paypal.token, this.paypal.payerId)
                    .then((paypalData) => {
                        this.paypalData = paypalData;
                        this.ensureShippingSelected();
                        this.updateCartInfo({
                            ...getSavedCartInfo() // session storage was updated outside of component
                        });
                        const throwIfNoShipping = true;
                        return this.setStateFromCart(throwIfNoShipping);
                    })
                    .then(() => {
                        return this.trackPaypalReturnEvents();
                    })
                    .catch((err) => {
                        this.endPaypalSession();
                        this.checkoutService.showErrorDialog(`Error with PayPal: ${err}`, 'Error');
                    })
                    .finally(() => {
                        this.loadingService.hide();
                    });
            }

            endPaypalSession() {
                this.$location.search('token', null);
                this.$location.search('PayerID', null);
                this.isReturnFromPaypal = false;
                this.setStateFromCart();
            }

            applyShippingMethodToCartIfNeeded() {
                if (!this.cartAndSavedInfo.shippingMethod) {
                    const selectedIndex = this.selectedShippingMethodIndex || 0;
                    const selectedShippingMethod = this.availableShippingMethods[selectedIndex];
                    const throwIfNoShipping = true;

                    this.cacheSelectedShippingMethod(selectedShippingMethod);
                    this.setStateFromCart(throwIfNoShipping);
                    this.getTax();
                }
                return this.$q.resolve();
            }

            placePaypalOrder() {
                const placeOrderOptions = {
                    cart: this.cartAndSavedInfo,
                    grandTotal: this.grandTotal,
                    paymentMethod: this.selectedPaymentMethod,
                    payPalPayerId: this.paypal.payerId,
                    payPalExpressCheckoutToken: this.paypal.token,
                    storeCreditInfo: this.storeCreditInfo,
                    shopperNote: this.orderNotes,
                    authorizeNet: {}
                };

                if (this.isLoggedIn) {
                    placeOrderOptions.customerId = this.apiService.getShopperId();
                }

                return this.orderService.placeOrder(placeOrderOptions)
                    .then((order) => {
                        this.order = order;
                        this.scrollToId('checkout');
                        clearSavedStoreCreditInfo();
                    });
            }

            onLoginClicked() {
                if (this.authService.useUniversalLogin()) {
                    this.authService.login();
                } else {
                    this.loginModalService.open()
                        .then(() => this.handleShopperLoggedIn());
                }
            }

            handleShopperLoggedIn() {
                this.loadingService.show('Getting account info...');
                return this.shopperAccountService.getAllShopperData()
                    .then(({ shopperAccount, shippingAddresses, paymentMethods }) => {
                        this.setAccountInfo(shopperAccount, shippingAddresses, paymentMethods);
                    }).finally(() => {
                        this.loadingService.hide();
                    });
            }

            initShopperAccount(isReturnFromPaypal) {
                this.authService.getShopperId().then((shopperId) => {
                    this.apiService.setShopperId(shopperId);
                    if (isReturnFromPaypal) {
                        this.apiService.account.get().then((shopperAccount) => {
                            this.loggedInShopper = shopperAccount;
                        });
                    } else {
                        this.loadingService.show('Getting account information...');
                        this.shopperAccountService.getAllShopperData()
                            .then(({ shopperAccount, shippingAddresses, paymentMethods }) => {
                                return this.setAccountInfo(shopperAccount, shippingAddresses, paymentMethods);
                            }).finally(() => {
                                this.loadingService.hide();
                            });
                    }
                });
            }

            updateCreditCardType(creditCardType) {
                this.creditCardType = creditCardType;
            }

            setAccountInfo(shopperAccount, shippingAddresses, paymentMethods) {
                if (paymentMethods) {
                    this.savedPaymentMethods = paymentMethods.items;
                }
                this.savedShippingAddresses = shippingAddresses;

                this.updateCartInfo();

                // don't overwrite the shipping address if they set one before logging in
                if (shippingAddresses &&
                    shippingAddresses.items &&
                    shippingAddresses.items.length &&
                    !this.cartAndSavedInfo.shippingAddress
                ) {
                    const defaultShippingAddress =
                        this.shopperAccountService.getDefaultShippingAddress(shippingAddresses);

                    if (defaultShippingAddress) {
                        this.updateCartInfo({
                            shippingAddress: defaultShippingAddress.address
                        });
                        this.setStateFromCart();
                    }
                }

                this.updateCartInfo({
                    contactInfo: {
                        email: shopperAccount.emailAddress
                            || (this.cartAndSavedInfo.contactInfo && this.cartAndSavedInfo.contactInfo.email),
                        phoneNumber: shopperAccount.phoneNumber
                            || (this.cartAndSavedInfo.contactInfo && this.cartAndSavedInfo.contactInfo.phoneNumber)
                    }
                });

                this.updateCartWithShopper(shopperAccount.shopperId, shopperAccount.emailAddress);

                this.isLoggedIn = true;
                this.loggedInShopper = shopperAccount;
                if (!this.isReturnFromPaypal && paymentMethods) {
                    const defaultPaymentMethod = find(paymentMethods.items, 'isDefault');
                    if (defaultPaymentMethod) {
                        this.setAccountPaymentInfo(defaultPaymentMethod);
                    }
                }
                this.contactInfoModel = { email: shopperAccount.emailAddress, phoneNumber: shopperAccount.phoneNumber };

                this.setStateFromCart();
                this.getTax();
            }

            setAccountPaymentInfo(paymentMethod) {
                if (!paymentMethod) return;
                this.selectedPaymentMethod = 'Credit Card';
                this.selectedCard = paymentMethod;
                this.accountBillingAddress = {
                    firstName: paymentMethod.name,
                    address1: paymentMethod.address.line1,
                    address2: paymentMethod.address.line2,
                    city: paymentMethod.address.city,
                    state: paymentMethod.address.state,
                    postalCode: paymentMethod.address.zip,
                    country: paymentMethod.address.country
                };
            }

            trackPaypalReturnEvents() {
                this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.contactInfo, 'PayPal', this.cartAndSavedInfo);
                this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.shippingAddress, 'PayPal', this.cartAndSavedInfo);
                this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.billingAddress, 'PayPal', this.cartAndSavedInfo);
                this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.paymentMethod, 'PayPal', this.cartAndSavedInfo);
                this.gaService.logCheckoutStep(this.GA_CHECKOUT_STEPS.shippingMethod, 'PayPal', this.cartAndSavedInfo);

                logCheckoutStarted(this.tenantId, this.cartAndSavedInfo, this.returnUrl);
                logShopperInfo(this.tenantId, this.cartAndSavedInfo);
                logShippingInfo(this.tenantId, this.cartAndSavedInfo);
                logPaymentMethod(this.tenantId, 'PayPal');
            }

            trackStepOneEvents() {
                this.gaService.logCheckoutStep(
                    this.GA_CHECKOUT_STEPS.contactInfo,
                    this.contactInfoModel.emailOptIn,
                    this.cartAndSavedInfo
                );
                this.gaService.logCheckoutStep(
                    this.GA_CHECKOUT_STEPS.shippingAddress,
                    this.shippingAddressModel.state,
                    this.cartAndSavedInfo
                );
            }

            trackStepTwoEvents() {
                this.gaService.logCheckoutStep(
                    this.GA_CHECKOUT_STEPS.shippingMethod,
                    this.cartAndSavedInfo.shippingMethod.name || 'none',
                    this.cartAndSavedInfo
                );
            }

            trackStepThreeEvents() {
                this.gaService.logCheckoutStep(
                    this.GA_CHECKOUT_STEPS.billingAddress,
                    this.billingSameAsShipping,
                    this.cartAndSavedInfo
                );
                this.gaService.logCheckoutStep(
                    this.GA_CHECKOUT_STEPS.paymentMethod,
                    this.selectedPaymentMethod,
                    this.cartAndSavedInfo
                );
            }

            shouldShowAccountPaymentAndBilling() {
                return this.isLoggedIn
                    && this.selectedPaymentMethod === 'Credit Card'
                    && angular.isDefined(this.selectedCard)
                    && angular.isDefined(this.accountBillingAddress);
            }

            shouldShowCheckoutWithPaypalButton() {
                return this.selectedPaymentMethod === 'PayPal' && !this.isReturnFromPaypal;
            }

            shouldShowEditStepTwoButton() {
                return this.steps.shippingMethod.complete
                    && this.selectedShippingMethodIndex !== noShippingMethodsIndex
                    && this.isShippingRequired;
            }

            shouldShowEditStepThreeButton() {
                return (this.isReturnFromPaypal && !this.paypalCancelled)
                    || (this.isLoggedIn && this.savedPaymentMethods && this.savedPaymentMethods.length > 0);
            }

            shouldShowPaymentMethodForm() {
                return (!this.isReturnFromPaypal || this.paypalCancelled)
                    && !this.selectedCard
                    && !this.accountBillingAddress;
            }

            shouldShowLoginButton() {
                return !this.steps.contactAndDelivery.complete && !this.isLoggedIn;
            }

            shouldShowSecondaryLoginButton() {
                return this.isReturnFromPaypal && this.steps.contactAndDelivery.complete && !this.isLoggedIn;
            }

            shouldShowPromoCodeLink() {
                return ((this.isMobile && !this.shouldShowEditStepThreeButton()) || !this.isMobile);
            }

            shouldStepOneBeOpen() {
                return this.currentStep === this.steps.contactAndDelivery.number
                    || this.steps.contactAndDelivery.complete;
            }

            shouldStepTwoBeOpen() {
                return this.currentStep === this.steps.shippingMethod.number
                    || this.steps.shippingMethod.complete;
            }

            shouldStepThreeBeOpen() {
                return this.currentStep === this.steps.paymentAndReview.number
                    || this.steps.paymentAndReview.complete;
            }

            shouldShowStepTwoContinueButton() {
                // TODO: reference appropriate availableShippingMethods object
                return this.steps.contactAndDelivery.complete && (
                    this.availableShippingMethods &&
                    this.availableShippingMethods.length > 0
                );
            }

            isContactAndDeliveryComplete() {
                if (this.cartAndSavedInfo.shippingAddress && this.cartAndSavedInfo.contactInfo) {
                    if (this.storeInfo.isCheckoutPhoneNumberRequired) {
                        return !!this.cartAndSavedInfo.contactInfo.email
                            && !!this.cartAndSavedInfo.contactInfo.phoneNumber;
                    }
                    return !!this.cartAndSavedInfo.contactInfo.email
                        && !!this.cartAndSavedInfo.shippingAddress.address1;
                }
                return false;
            }

            cartGrandTotalInCents() {
                const cartTotalAmountInCents = getCartTotalAmountInCents(this.cart);
                const taxAmountInCents = get(this, 'taxAmountInCents', 0);
                const shippingAmountInCents = get(this.cartAndSavedInfo, 'shippingMethod.shippingPriceInCents', 0);

                return cartTotalAmountInCents + taxAmountInCents + shippingAmountInCents;
            }

            cartGrandTotal() {
                return this.cartGrandTotalInCents() / 100;
            }

            shouldShowPaymentTypeSelection() {
                return this.grandTotal > 0;
            }

            updateCartInfo(cartInfoChanges) {
                this.cartAndSavedInfo = {
                    ...this.cart,
                    ...this.cartAndSavedInfo,
                    ...cartInfoChanges
                };
                setSavedCartInfo(this.cartAndSavedInfo);
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/newCheckout/newCheckout.component.js